import type { FC } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import type { ButtonTextProps } from '@segment/matcha/components/ButtonText'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const BuildCustomerTrust: FC = () => {
  const pageConfig = usePageConfig()

  const copy = useMemo(
    () => ({
      title: `Build customer trust and take a proactive approach to privacy`,
      columns: [
        {
          title: `Privacy Portal`,
          content: `With the Segment Privacy Portal, you can automate your approach to keeping your customers' data private. Gain real-time visibility into the personal information you collect, its sources, and destinations. Set rules to automatically protect it.`,
          buttonTitle: `Explore Privacy Portal`,
          buttonHref: getAbsoluteUrl(`/product/privacy-portal/`, pageConfig),
          target: `_self`,
        },
        {
          title: `Certifications and attestations`,
          content: `Segment’s security and privacy program is based on and aligned with industry-standard frameworks, and we maintain a comprehensive suite of certifications and attestations to further demonstrate our commitment to security and privacy.`,
          buttonTitle: `View certificates`,
          buttonHref: `https://assets.ctfassets.net/9u2t3ap6pctb/6nPHU2NWIvn2TkBo0CulHa/329a41b7cbd7edfbe9a42cd9973040d7/Segment_ISO_27001_Certificate.pdf`,
          target: `_blank`,
        },
      ],
    }),
    [pageConfig]
  )

  return (
    <SectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.title}
          </CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <StyledSectionBase>
        <StyledColumnTrack
          overrides={{
            md: 2,
          }}
        >
          {copy.columns.map((column, index) => (
            <CopyTextBase key={index}>
              <CopyText.Heading tag="h3" variant="h4">
                {column.title}
              </CopyText.Heading>
              <CopyText.Description tag="p" variant="pMedium">
                {column.content}
              </CopyText.Description>
              <CopyText.Link href={column.buttonHref} target={column.target as ButtonTextProps[`target`]}>
                {column.buttonTitle}
              </CopyText.Link>
            </CopyTextBase>
          ))}
        </StyledColumnTrack>
      </StyledSectionBase>
    </SectionBase>
  )
}

const StyledSectionBase = styled(Section.Contents)`
  max-width: 1120px;
`

const StyledColumnTrack = styled(ColumnTrack)`
  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    gap: 80px;
  }
`

export default BuildCustomerTrust
